import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/hooks/useLanguage";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieConsent } from "@/components/CookieConsent";
import { Loader2 } from "lucide-react";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Login = lazy(() => import("./pages/admin/Login"));
const ForgotPassword = lazy(() => import("./pages/admin/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const Setup = lazy(() => import("./pages/admin/Setup").then(m => ({ default: m.Setup })));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Templates = lazy(() => import("./pages/admin/Templates"));
const TemplateEditor = lazy(() => import("./pages/admin/TemplateEditor"));
const ReferralLanding = lazy(() => import("./pages/referral/index"));
const ReferralRegister = lazy(() => import("./pages/referral/Register"));
const ReferralLogin = lazy(() => import("./pages/referral/Login"));
const ReferralDashboard = lazy(() => import("./pages/referral/Dashboard"));
const Catalog = lazy(() => import("./pages/Catalog"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route path="/admin/setup" element={<Setup />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute requireAdmin>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/templates"
                element={
                  <ProtectedRoute requireAdmin>
                    <Templates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/templates/new"
                element={
                  <ProtectedRoute requireAdmin>
                    <TemplateEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/templates/:id/edit"
                element={
                  <ProtectedRoute requireAdmin>
                    <TemplateEditor />
                  </ProtectedRoute>
                }
              />
              {/* Referral Program Routes */}
              <Route path="/referral" element={<ReferralLanding />} />
              <Route path="/referral/register" element={<ReferralRegister />} />
              <Route path="/referral/login" element={<ReferralLogin />} />
              <Route path="/referral/dashboard" element={<ReferralDashboard />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ScrollToTop />
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
